/**
 * @file new-credit-card.component.ts
 */
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { PharmacyDetailComponent } from 'src/app/main/find-pharmacy/pharmacy-detail/pharmacy-detail.component';
import { firstLastNamePattern, states } from 'src/app/main/main.constants';
import { AppInsightsService } from 'src/app/services/app-insights/app-insights.service';
import { OrderService } from 'src/app/services/order/order.service';
import { UserService } from '../../services/user/user.service';
import * as cardType from 'credit-card-type';
import { MultilingualService } from 'src/app/core/services/multilingual/multilingual.service';
import { AppCenterAnalytics, AppCenterAnalyticsAPIMsgs } from '../../services/app-insights/app-insights';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { CreateCreditCardRequest } from 'src/app/interfaces/credit-card.interface';
import { getLastNChars } from '../util';

@Component({
  selector: 'app-new-credit-card',
  templateUrl: './new-credit-card.component.html',
  styleUrls: []
})
export class NewCreditCardComponent implements OnInit, OnDestroy {
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;

  subscription = new Subscription();

  isLoading = false;
  isError = false;
  cardForm: UntypedFormGroup;
  states = states;
  errorMsg!: string;
  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly dialogRef: MatDialogRef<PharmacyDetailComponent>,
    private readonly orderService: OrderService,
    private readonly appInsightsService: AppInsightsService,
    private readonly userService: UserService,
    public readonly multilingualService: MultilingualService,
    private readonly snackBar: MatSnackBar,
  ) {
    this.cardForm = this.buildAddressForm();
  }

  ngOnInit(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.ADD_PAYMENT_FORM_VIEWED);
    this.detectCard();
  }

  buildAddressForm(): UntypedFormGroup {
    return this.fb.group({
      addressLine1: ['', [Validators.required]],
      addressLine2: [''],
      cardId: [this.userService.eligibilityVerificationResponse?.cardId, [Validators.required]],
      city: ['', [Validators.required]],
      clientId: [this.userService.eligibilityVerificationResponse?.clientId, [Validators.required]],
      creditCardNumber: ['', [Validators.required, Validators.minLength(16), Validators.maxLength(16)]],
      creditCardType: ['', [Validators.required]],
      defaultCardIndicator: [false],
      expirationDate: ['', [Validators.required]],
      firstNameOnCard: ['', [Validators.required, Validators.pattern(firstLastNamePattern)]],
      lastNameOnCard: ['', [Validators.required, Validators.pattern(firstLastNamePattern)]],
      personCode: [this.userService.eligibilityVerificationResponse?.personCode, [Validators.required]],
      state: ['', [Validators.required]],
      zipCode: ['', [Validators.required, Validators.minLength(5)]],
    });
  }

  get addressLine1(): UntypedFormControl {
    return this.cardForm.get('addressLine1') as UntypedFormControl;
  }

  get cardId(): UntypedFormControl {
    return this.cardForm.get('cardId') as UntypedFormControl;
  }

  get city(): UntypedFormControl {
    return this.cardForm.get('city') as UntypedFormControl;
  }

  get clientId(): UntypedFormControl {
    return this.cardForm.get('clientId') as UntypedFormControl;
  }

  get creditCardNumber(): UntypedFormControl {
    return this.cardForm.get('creditCardNumber') as UntypedFormControl;
  }

  get creditCardType(): UntypedFormControl {
    return this.cardForm.get('creditCardType') as UntypedFormControl;
  }

  get defaultCardIndicator(): UntypedFormControl {
    return this.cardForm.get('defaultCardIndicator') as UntypedFormControl;
  }

  get expirationDate(): UntypedFormControl {
    return this.cardForm.get('expirationDate') as UntypedFormControl;
  }

  get firstNameOnCard(): UntypedFormControl {
    return this.cardForm.get('firstNameOnCard') as UntypedFormControl;
  }

  get lastNameOnCard(): UntypedFormControl {
    return this.cardForm.get('lastNameOnCard') as UntypedFormControl;
  }

  get personCode(): UntypedFormControl {
    return this.cardForm.get('personCode') as UntypedFormControl;
  }

  get state(): UntypedFormControl {
    return this.cardForm.get('state') as UntypedFormControl;
  }

  get zipCode(): UntypedFormControl {
    return this.cardForm.get('zipCode') as UntypedFormControl;
  }

  onDateChange(date: string): void {
    const dateArr = date.split('/');
    let mm = dateArr[0];
    const yy = dateArr[1];

    if (+mm > 12) {
      mm = '12';
      this.expirationDate.setValue(mm + '/' + yy);
    }
  }

  addCard(): void {
    this.isLoading = true;
    this.isError = false;
    if (this.defaultCardIndicator.value) {
      this.defaultCardIndicator.setValue('YES');
    } else {
      this.defaultCardIndicator.setValue('NO');
    }
    let date = this.expirationDate.value;
    date = date.split('');
    date.splice(2, 0, '/');
    date = date.join('');
    const body: CreateCreditCardRequest = this.cardForm.value;
    body.expirationDate = date;
    body.city = body.city.toUpperCase();

    const logData = JSON.parse(JSON.stringify(body));
    logData.creditCardNumber = getLastNChars(logData.creditCardNumber, 4);
    delete logData.expirationDate;

    let responseText = AppCenterAnalyticsAPIMsgs.COMMON_INITIAL_MSG;
    this.orderService.createCreditCard(body).subscribe((res) => {
      responseText = AppCenterAnalyticsAPIMsgs.SUCCESS_ADD_PAYMENT_METHOD;
      this.appInsightsService.trackPageView(
        `${AppCenterAnalytics.ADD_CREDIT_CARD_FORM_VIEWED} ${AppCenterAnalytics.COMMON_API_CALL}`,
        { formData: logData, Response: responseText }
      );
      this.dialogRef.close({ code: 0 });
    }, (error) => {
      this.isError = true;
      this.isLoading = false;
      this.errorMsg = error;
      responseText = error ? error : AppCenterAnalyticsAPIMsgs.ERROR_ADD_PAYMENT_METHOD;
      if (this.defaultCardIndicator.value === 'YES') {
        this.defaultCardIndicator.setValue(true);
      } else {
        this.defaultCardIndicator.setValue(false);
      }
      this.appInsightsService.trackPageView(
        `${AppCenterAnalytics.ADD_CREDIT_CARD_FORM_VIEWED} ${AppCenterAnalytics.COMMON_API_CALL}`,
        { formData: logData, Response: responseText }
      );

      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    });
  }

  detectCard(): void {
    this.subscription.add(this.creditCardNumber.valueChanges.subscribe((val) => {
      const cards = cardType(val);
      if (cards.length) {
        if (cards[0].type === 'american-express') {
          this.creditCardType.setValue('AMERICAN_EXPRESS');
          this.cardForm.controls['creditCardNumber'].setValidators([Validators.minLength(15), Validators.maxLength(16), Validators.required])
        } else if (cards[0].type === 'mastercard') {
          this.creditCardType.setValue('MASTERCARD');
          this.cardForm.controls['creditCardNumber'].setValidators([Validators.minLength(16), Validators.maxLength(16), Validators.required])
        } else if (cards[0].type === 'discover') {
          this.creditCardType.setValue('DISCOVER');
          this.cardForm.controls['creditCardNumber'].setValidators([Validators.minLength(16), Validators.maxLength(16), Validators.required]);
        } else if (cards[0].type === 'visa') {
          this.creditCardType.setValue('VISA');
          this.cardForm.controls['creditCardNumber'].setValidators([Validators.minLength(16), Validators.maxLength(16), Validators.required]);
        } else {
          this.creditCardType.setValue('');
          this.cardForm.controls['creditCardNumber'].setValidators([Validators.minLength(16), Validators.maxLength(16), Validators.required]);
        }
      } else {
        this.cardForm.controls['creditCardNumber'].setValidators([Validators.minLength(16), Validators.maxLength(16), Validators.required]);
      }
    }));

    this.subscription.add(this.creditCardType.valueChanges.subscribe((val) => {
      if (val === 'AMERICAN_EXPRESS') {
        const creditCardNumberControl = this.cardForm.get('creditCardNumber');
        creditCardNumberControl?.setValidators([Validators.minLength(15), Validators.maxLength(16), Validators.required]);
        creditCardNumberControl?.updateValueAndValidity({ emitEvent: false });
      } else {
        const creditCardNumberControl = this.cardForm.get('creditCardNumber');
        creditCardNumberControl?.setValidators([Validators.minLength(16), Validators.maxLength(16), Validators.required]);
        creditCardNumberControl?.updateValueAndValidity({ emitEvent: false });
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
